import { Link } from 'react-router-dom';
import { CCard, CCheckbox } from 'components/atoms';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Tooltip } from '@mui/material';
dayjs.extend(relativeTime);
import { ReactComponent as CalendarSvg } from 'assets/svg/calendar.svg';
import { ReactComponent as ReportSvg } from 'assets/svg/report.svg';
import { ReactComponent as VaccineNotificationSvg } from 'assets/svg/vaccine-notification.svg';
import { ReactComponent as MedicineSvg } from 'assets/svg/medicine.svg';
import { ReactComponent as RepresentativeSvg } from 'assets/svg/representative.svg';
import { ReactComponent as BellSvg } from 'assets/svg/bell.svg';
import { ReactComponent as ExportSvg } from 'assets/svg/export.svg';

import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { setVideoCall } from 'store/app/app-slice';
import { setProfile } from 'store/auth/auth-slice';
import { useEffect } from 'react';

enum NotificationGroupTypeId {
  Appointment = 1,
  Report = 2,
  Vaccine = 3,
  Medicine = 4,
  Service = 5,
  ChildProfile = 6,
  Message = 7,
  Call = 8,
  PetRepresentative = 9,
}

enum NotificationStatus {
  NotRead = 1,
  Read = 2,
  CouldNotSend = 3,
}
// backend'deki routing listesi ile eslesen enum
enum NotificationRouting {
  Appointments = 'AppoinmentListScreen',
  PatientReports = 'PatientReports',
  VaccineCard = 'VaccineScreen',
  StaffCall = 'StaffCallScreen',
  Medicine = 'MedicineScreen',
  ContactUsScreen = 'ContactUsScreen',
  //Subscriptions = 'Subscriptions', //
  //PatientSubscriptions = 'PatientSubscriptions',
  PatientSubscriptionsSettings = 'PatientSubscriptionsSettings',
  PatientSubscriptionUpdateCard = 'PatientSubscriptionUpdateCard',
  //PatientSubscriptionCancellations = 'PatientSubscriptionCancellations',
}

type NotificationProps = {
  notification: NotificationResp;
  isChecked: boolean;
  onClick: () => void;
  closeDrawer: () => void;
  onMarkAsRead: (id: number) => Promise<void>;
  openUpdateCardDrawer: (membership: {
    patientId?: number;
    subscriptionGroupId?: number;
    subscriptionId?: number;
    patientSubscriptionId?: number;
    cancellationId?: number;
  }) => void;
};

enum NotificationActionType {
  NAVIGATE = 'navigate',
  MODAL = 'modal',
  EXTERNAL = 'external',
  CALLBACK = 'callback',
  NONE = 'none',
}

interface NotificationAction {
  type: NotificationActionType;
  payload: any;
}

export default function Notification({
  notification,
  isChecked,
  onClick,
  closeDrawer,
  onMarkAsRead,
  openUpdateCardDrawer,
}: NotificationProps) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    return () => {
      dispatch(setProfile({ profileId: 0 }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const videoCall = useAppSelector((state) => state.app.videoCall);

  const handleNotificationAction = (stringifyJson: string): NotificationAction => {
    const json: {
      Routing?: NotificationRouting;
      PatientId?: number; // for subscription
      SubscriptionGroupId?: number; // for subscription
      SubscriptionId?: number; // for subscription
      PatientSubscriptionId?: number; // for subscription
      CancellationId?: number; // for subscription
    } = stringifyJson ? JSON.parse(stringifyJson) : '';

    switch (json.Routing) {
      case NotificationRouting.Appointments:
        return {
          type: NotificationActionType.NAVIGATE,
          payload: { path: `/appointments` },
        };
      case NotificationRouting.PatientReports:
        return {
          type: NotificationActionType.NAVIGATE,
          payload: { path: `/reports` },
        };
      case NotificationRouting.VaccineCard:
        return {
          type: NotificationActionType.NAVIGATE,
          payload: { path: `/vaccines` },
        };
      case NotificationRouting.Medicine:
        return {
          type: NotificationActionType.NAVIGATE,
          payload: { path: `/medicines` },
        };
      case NotificationRouting.StaffCall:
        return {
          type: NotificationActionType.NAVIGATE,
          payload: { path: `/staff-call` },
        };
      case NotificationRouting.ContactUsScreen:
        return {
          type: NotificationActionType.NAVIGATE,
          payload: { path: `/common/contact` },
        };
      case NotificationRouting.PatientSubscriptionsSettings:
        return {
          type: NotificationActionType.NAVIGATE,
          payload: { path: `/account?tab=5` },
        };
      case NotificationRouting.PatientSubscriptionUpdateCard:
        return {
          type: NotificationActionType.MODAL,
          payload: {
            modalType: 'updateCard',
            data: {
              patientId: json.PatientId,
              subscriptionGroupId: json.SubscriptionGroupId,
              subscriptionId: json.SubscriptionId,
              patientSubscriptionId: json.PatientSubscriptionId,
              cancellationId: json.CancellationId,
            },
          },
        };
      default:
        return {
          type: NotificationActionType.NONE,
          payload: {},
        };
    }
  };

  const action = handleNotificationAction(notification.additionalData);

  const handleTitleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notification.status === NotificationStatus.NotRead) {
      onMarkAsRead(notification.id);
    }

    switch (action.type) {
      case NotificationActionType.MODAL:
        if (action.payload.modalType === 'updateCard') {
          openUpdateCardDrawer?.(action.payload.data);
        }
        // add here other modal types if any
        break;

      case NotificationActionType.NAVIGATE:
        closeDrawer();
        // navigate to the path
        break;

      case NotificationActionType.EXTERNAL:
        window.open(action.payload.url, '_blank', 'noopener,noreferrer');
        break;

      case NotificationActionType.CALLBACK:
        action.payload.callback();
        break;
    }
  };

  const renderNotificationTitle = () => {
    if (action.type === NotificationActionType.NAVIGATE) {
      return (
        <Link
          to={action.payload.path}
          className="flex items-center gap-1 hover:underline"
          onClick={handleTitleClick}
        >
          {notification.title}
          <ExportSvg className="h-4 w-4" />
        </Link>
      );
    }
    if (action.type === NotificationActionType.MODAL) {
      return (
        <button type="button" className="flex items-center gap-1 text-left" onClick={handleTitleClick}>
          {notification.title}
          <ExportSvg className="h-4 w-4" />
        </button>
      );
    }

    return <span>{notification.title}</span>;
  };

  return (
    <CCard
      className={`hover-transition-xs p-4 !shadow-c1 hover:!shadow-c12 ${
        notification.status === NotificationStatus.Read ? 'text-neutral-200' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <CCheckbox
            checked={isChecked}
            iconType="rounded"
            className="text-neutral-200"
            iconClassName="w-4 h-4"
          />
          <div
            className={`flex flex-1 items-center justify-between ${
              notification.status === NotificationStatus.Read ? 'text-neutral-200' : 'text-primary-300'
            }`}
          >
            <div className="flex items-center gap-2 text-label16">
              <NotificationIcon
                status={notification.status}
                type={notification.notificationGroupTypeId}
              />
              {renderNotificationTitle()}
            </div>

            <Tooltip title={dayjs(notification.sentDate).format('DD MMMM YYYY HH:mm')}>
              <span className="text-body14 leading-normal">
                {dayjs(notification.sentDate).fromNow()}
              </span>
            </Tooltip>
          </div>
        </div>

        {notification.type == 2 ? (
          <p
            className={`ml-7 text-body16 ${
              notification.status === NotificationStatus.Read ? 'text-neutral-200' : 'text-neutral-500'
            }`}
            onClick={() => {
              closeDrawer();
              const additionalData = JSON.parse(notification.additionalData);

              dispatch(
                setVideoCall({
                  ...videoCall,
                  isShow: true,
                  isAccessGranted: false,
                  appointmentDate: additionalData.AppointmentDate,
                  doctorCode: additionalData.DoctorCode,
                  patientName: additionalData.PatientName,
                  protocolNo: additionalData.ProtocolNo,
                  videoCallGroupId: additionalData.GroupId,
                  doctorName: additionalData.DoctorName,
                  // doctorPhoto: appointment.doctorPhoto,
                  // doctorTitle: appointment.doctorTitle,
                })
              );
            }}
          >
            {notification.description}
          </p>
        ) : (
          <p
            className={`ml-7 text-body16 ${
              notification.status === NotificationStatus.Read ? 'text-neutral-200' : 'text-neutral-500'
            }`}
          >
            {notification.description}
          </p>
        )}
      </div>
    </CCard>
  );
}

type NotificationIconProps = {
  status: NotificationResp['status'];
  type: NotificationResp['notificationGroupTypeId'];
};

function NotificationIcon({ status, type }: NotificationIconProps) {
  switch (type) {
    case NotificationGroupTypeId.Appointment:
      return (
        <div className="relative h-6 w-6">
          <CalendarSvg className="h-full w-full" />
          <UnreadIcon status={status} />
        </div>
      );
    case NotificationGroupTypeId.Report:
      return (
        <div className="relative h-6 w-6">
          <ReportSvg className="h-full w-full" />
          <UnreadIcon status={status} />
        </div>
      );
    case NotificationGroupTypeId.Vaccine:
      return (
        <div className="relative h-6 w-6">
          <VaccineNotificationSvg className="h-full w-full" />
          <UnreadIcon status={status} />
        </div>
      );
    case NotificationGroupTypeId.Medicine:
      return (
        <div className="relative h-6 w-6">
          <MedicineSvg className="h-full w-full" />
          <UnreadIcon status={status} />
        </div>
      );
    case NotificationGroupTypeId.PetRepresentative:
      return (
        <div className="relative h-6 w-6">
          <RepresentativeSvg className="h-full w-full" />
          <UnreadIcon status={status} />
        </div>
      );
    default:
      return (
        <div className="relative h-6 w-6">
          <BellSvg className="h-full w-full" />
          <UnreadIcon status={status} />
        </div>
      );
  }
}

function UnreadIcon({ status }: { status: NotificationIconProps['status'] }) {
  return status === NotificationStatus.NotRead ? (
    <span className="absolute right-0 top-0 h-2 w-2 rounded-full bg-secondary-800" />
  ) : null;
}

